import type { RefresherEventDetail } from '@ionic/core/components';
import {
  IonContent,
  IonPage,
  IonItem,
  IonToolbar,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  IonSearchbar,
} from '@ionic/react';
import { chevronDownCircleOutline } from 'ionicons/icons';
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Clock from '../../assets/Clock.svg';
import CreateAsset from '../../assets/CreateAsset.svg';
import axios from '../../axios';
import Header from '../../components/Header';
import NoRecords from '../../components/NoRecords';
import ScanButton from '../../components/ScanButton';
import { AppContext } from '../../providers/AppContext';


const MaintenanceHome: React.FC = () => {
  const { t } = useTranslation();

  const { state } = useContext(AppContext);

  const isMounted = useRef(true);

  const [data, setData] = useState<any>([]);

  const [isLoading, setIsLoading] = useState(true);

  const [searchText, setSearchText] = useState('');

  const getData = async (context: any = {}) => {
    isMounted.current = true; // Set isMounted to true
    let url = '/method/preflet_asset.api.get_assigned_maintenance?limit=5&start=0'

    // add search data if any
    if ('search' in context || searchText) {
      if (isMounted.current)
        setSearchText(context.search || '');
      if (context.search) url += `&search=${context.search || searchText}`;
    }

    const config: any = {
      method: 'get',
      url,
      headers: {
        Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
      },
    };
    try {
      const res = await axios(config);
      return res?.data?.message?.maintenance_list || []
    } catch (err) {
      return []
    } finally {
      if (isMounted?.current)
        setIsLoading(false);
    }
  };


  useEffect(() => {

    (async () => {
      try {
        const data = await getData();
        setData(data);
      } catch (err) {
        console.log('Error occured when fetching maintenance');
      }
    })();

    // Cleanup function
    return () => {
      isMounted.current = false; // Mark the component as unmounted when it's being cleaned up
      // You can also cancel any subscriptions or clean up other resources here
    };
  }, []);


  function doRefresh(event: CustomEvent<RefresherEventDetail>) {
    (async () => {
      try {
        const d = await getData();
        setData(d);
      } catch (err) {
        console.log(err);
      } finally {
        setTimeout(() => {
          console.log('Async operation has ended');
          event.detail.complete();
        }, 2000);
      }
    })();
  }

  const handleSearch = async (value: any) => {
    setIsLoading(true);
    const d = await getData({ search: value, isFiltered: true });
    setData(d);
  };




  const getStatusBadge = (item: any) => {
    const isOverdue = item.target_date && new Date(item.target_date).toLocaleDateString() < new Date().toLocaleDateString();
    return isOverdue;
  };

  return (
    <IonPage>
      <Header enableBackButton='' title="" />
      <IonContent fullscreen={true}>
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={t('Utils.pullRefresh')}
            refreshingSpinner="circles"
            refreshingText={t('Utils.refreshing')}
          />
        </IonRefresher>

        {/* ── Hero Banner ── */}
        <div style={{
          background: 'linear-gradient(135deg, #1d508d 0%, #0f3460 100%)',
          padding: '16px 16px 16px',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <div style={{ position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

          <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.12em', textTransform: 'uppercase', position: 'relative', zIndex: 1, marginBottom: '1rem' }}>
            {t('Maintenance.Recent')}
          </p>
          {/* <h1 style={{ margin: '3px 0 12px', fontSize: 22, fontWeight: 800, color: '#fff', lineHeight: 1.15, position: 'relative', zIndex: 1 }}>
            Maintenance
          </h1> */}

          {/* Quick action buttons inside banner */}
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, position: 'relative', zIndex: 1 }}>
            {!!state.permissions.maintenance_request_create && (
              <Link to="/createMainten1" style={{ textDecoration: 'none', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', borderRadius: 14, padding: '10px 16px', boxShadow: '0 4px 16px rgba(29,80,141,0.15)', border: '1.5px solid rgba(29,80,141,0.1)', cursor: 'pointer' }}>
                  <img src={CreateAsset} alt="create" style={{ width: 18, height: 18 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1d508d', whiteSpace: 'nowrap' }}>{t('Maintenance.Create')}</span>
                </div>
              </Link>
            )}
            <Link to="/maintenanceHistory" style={{ textDecoration: 'none', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 14, padding: '10px 16px', border: '1.5px solid rgba(255,255,255,0.3)', cursor: 'pointer' }}>
                <img src={Clock} alt="history" style={{ width: 16, height: 16, filter: 'brightness(10)' }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>{t('Maintenance.History.History')}</span>
              </div>
            </Link>
          </div>
        </div>

        {/* ── Search ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 12px 4px' }}>
          <IonSearchbar
            value={searchText}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            onIonChange={(e) => handleSearch(e.detail.value!)}
            placeholder={`${t('Maintenance.Recent')}`}
            style={{ flex: 1, '--border-radius': '14px', '--box-shadow': '0 2px 8px rgba(0,0,0,0.06)', '--background': '#fff', padding: 0 } as any}
          />
        </div>

        {/* ── Section Header ── */}
        <div style={{ padding: '8px 16px 4px' }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#1d508d' }}>{t('Maintenance.Recent')}</p>
        </div>

        {/* ── Skeleton Loaders ── */}
        {isLoading && [1, 2, 3, 4, 5].map((element) => (
          <div key={element} style={{ margin: '0 12px 10px', background: '#fff', borderRadius: 16, padding: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <IonSkeletonText animated style={{ width: '55%', height: 16, borderRadius: 8 }} />
              <IonSkeletonText animated style={{ width: '20%', height: 16, borderRadius: 8 }} />
            </div>
            <IonSkeletonText animated style={{ width: '40%', height: 12, borderRadius: 8 }} />
          </div>
        ))}

        {/* ── Maintenance Cards ── */}
        <div style={{ padding: '0 12px' }}>
          {data && data.length > 0 && data.map((item: any, key: any) => {
            const isOverdue = getStatusBadge(item);
            return (
              <div key={key} style={{
                background: '#fff',
                borderRadius: 18,
                padding: '14px 16px',
                marginBottom: 10,
                boxShadow: '0 3px 14px rgba(0,0,0,0.07)',
                border: `1.5px solid ${isOverdue ? 'rgba(224,60,90,0.15)' : 'rgba(29,80,141,0.07)'}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 12,
              }}>
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1d2d44' }}>{item.name}</p>
                    {isOverdue && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(224,60,90,0.1)', color: '#e03c5a', border: '1px solid rgba(224,60,90,0.25)', whiteSpace: 'nowrap' }}>
                        {t('Assets.Details.overdue')}
                      </span>
                    )}
                  </div>
                  {item.asset && (
                    <p style={{ margin: 0, fontSize: 12, color: '#6b7b8f' }}>
                      {t('Assets.Details.AID')}: <span style={{ fontWeight: 600, color: '#1d508d' }}>{item.asset}</span>
                    </p>
                  )}
                </div>

                <div style={{ textAlign: 'right', flexShrink: 0, maxWidth: '40%' }}>
                  <p style={{ margin: '0 0 6px', color: '#6b7b8f', fontSize: 12, fontWeight: 600 }}>
                    {t(`Utils.${item.doctype}`)}
                  </p>
                  <Link
                    to={{ pathname: '/maintainanceDetail', state: item?.name, search: 'back=maintenanceHome' }}
                    style={{
                      display: 'inline-block',
                      background: 'rgba(29,80,141,0.08)',
                      color: '#1d508d',
                      padding: '4px 12px',
                      borderRadius: 14,
                      fontSize: 12,
                      fontWeight: 700,
                      textDecoration: 'none',
                      marginBottom: 6,
                    }}
                  >
                    {t('Assets.Details.Details')}
                  </Link>
                  {item.men_status && (
                    <p style={{ margin: 0, color: '#929bab', fontSize: 11, fontWeight: 600 }}>
                      {item.men_status}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {data && data.length > 0 && (
            <Link
              to="/maintenanceHistory"
              style={{ display: 'block', textAlign: 'center', margin: '4px 0 24px', fontSize: 13, fontWeight: 600, color: '#1d508d', textDecoration: 'none', borderBottom: '1.5px solid rgba(29,80,141,0.3)', width: 'fit-content', marginInline: 'auto' }}
            >
              {t('Utils.viewAll')}
            </Link>
          )}
        </div>

        {/* ── Empty State ── */}
        {!isLoading && data.length === 0 && (
          <IonRow>
            <NoRecords width="30%" smallText={true} />
          </IonRow>
        )}

        <ScanButton isAssetPage={false} />
      </IonContent>
    </IonPage>
  );
};

export default MaintenanceHome;
